import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface SubscriptionContextType {
  user: User | null;
  isSubscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  purchasedProducts: string[];
  isLoading: boolean;
  monthlyCredits: number;
  purchasedCredits: number;
  totalCredits: number;
  creditsResetDate: string | null;
  isTrialActive: boolean;
  trialEndDate: string | null;
  checkSubscription: () => Promise<void>;
  checkCredits: () => Promise<void>;
  deductCredits: (amount: number) => Promise<boolean>;
  createCheckout: (priceId: string) => Promise<string>;
  createPayment: (priceId: string) => Promise<string>;
  purchaseCredits: (priceId: string) => Promise<string>;
  openCustomerPortal: () => Promise<void>;
  hasPremiumAccess: () => boolean;
  hasFeatureAccess: (featureProductId: string) => boolean;
  hasAITherappiAccess: () => boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const PRODUCTS = {
  FOUNDATIONS: 'prod_TAZPXkiLL1pD8u',
  MASTERY: 'prod_TAZPpPxlUlSDGn',
  AI_THERAPPI_STANDALONE: 'prod_TAZPNU6FJXvArq',
  AI_THERAPPI_FOUNDATIONS_ADDON: 'prod_TAZQgZrKKRf0p0',
  AI_THERAPPI_MASTERY_ADDON: 'prod_TAZQwkKozLIErj',
  // Legacy products
  STANDARD: 'prod_T9XDN7kDQ18Mil',
  PREMIUM: 'prod_T9XDuiwDoVR23n',
  MENSTRUAL_TRACKER: 'prod_T4f1ZOTTopHUTw',
};

const PRICES = {
  FOUNDATIONS: 'price_1SEEGHDADiSUiGodWuALEof9',
  MASTERY: 'price_1SEEGYDADiSUiGodBhd8Gtgs',
  AI_THERAPPI_STANDALONE: 'price_1SEEH4DADiSUiGod8KwavKx5',
  AI_THERAPPI_FOUNDATIONS_ADDON: 'price_1SEEHHDADiSUiGodUe9GCnwF',
  AI_THERAPPI_MASTERY_ADDON: 'price_1SEEHRDADiSUiGodr2B0zycw',
  // Legacy prices
  STANDARD: 'price_1SDE99DADiSUiGodes78dq76',
  PREMIUM: 'price_1SDE9ODADiSUiGodlayFTMVC',
  MENSTRUAL_TRACKER: 'price_1SnYCqDADiSUiGodhVWHUqBH',
};

// VIP emails that get full access (lowercase for comparison)
const VIP_EMAILS = [
  'amanda@thepsychicpsychologist.com',
  'mayastephenson@live.co.uk',
  'darrenleeatkinson@hotmail.com',
  'lylastephenson@live.co.uk',
  'amandacharles@sky.com', // admin
];

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [purchasedProducts, setPurchasedProducts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [monthlyCredits, setMonthlyCredits] = useState(0);
  const [purchasedCredits, setPurchasedCredits] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);
  const [creditsResetDate, setCreditsResetDate] = useState<string | null>(null);
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState<string | null>(null);

  const checkSubscription = async () => {
    try {
      if (!user) return;
      
      // Get current session to ensure we have a valid token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // No active session - silently return
        return;
      }
      
      // Guard: don't call if token is missing or clearly invalid
      if (!session.access_token || session.access_token.split('.').length !== 3) {
        return;
      }

      // Check subscription status with auth token
      const { data: subData, error: subError } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      if (subError) {
        // Error checking subscription - handled silently
      } else {
        setIsSubscribed(subData?.subscribed || false);
        setProductId(subData?.product_id || null);
        setSubscriptionEnd(subData?.subscription_end || null);
      }

      // Check one-time purchases
      const { data: purchases, error: purchaseError } = await supabase
        .from('user_purchases')
        .select('product_id')
        .eq('status', 'completed');

      if (purchaseError) {
        // Error fetching purchases - handled silently
      } else {
        setPurchasedProducts(purchases?.map(p => p.product_id) || []);
      }
    } catch {
      // Error in checkSubscription - handled silently
    }
  };

  const createCheckout = async (priceId: string): Promise<string> => {
    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: { price_id: priceId }
    });

    if (error) throw error;
    return data.url;
  };

  const createPayment = async (priceId: string): Promise<string> => {
    const { data, error } = await supabase.functions.invoke('create-payment', {
      body: { price_id: priceId }
    });

    if (error) throw error;
    return data.url;
  };

  const openCustomerPortal = async () => {
    const { data, error } = await supabase.functions.invoke('customer-portal');
    
    if (error) throw error;
    window.open(data.url, '_blank');
  };

  const hasPremiumAccess = (): boolean => {
    // Mastery, Foundations, Premium or Standard subscription grants access
    return isSubscribed && (
      productId === PRODUCTS.MASTERY || 
      productId === PRODUCTS.FOUNDATIONS ||
      productId === PRODUCTS.PREMIUM || 
      productId === PRODUCTS.STANDARD
    );
  };

  const hasFeatureAccess = (featureProductId: string): boolean => {
    // VIP users get full access
    if (user?.email && VIP_EMAILS.includes(user.email.toLowerCase())) return true;
    
    // Trial users get access to everything
    if (isTrialActive) return true;
    
    // Mastery and Premium have access to everything
    if (productId === PRODUCTS.MASTERY || productId === PRODUCTS.PREMIUM) return true;
    
    // Foundations and Standard have access to core features
    if (productId === PRODUCTS.FOUNDATIONS || productId === PRODUCTS.STANDARD) {
      return featureProductId !== 'premium_course' && featureProductId !== 'premium_meditation';
    }
    
    // Check individual purchases (including AI Therappi add-ons)
    return purchasedProducts.includes(featureProductId);
  };

  const hasAITherappiAccess = (): boolean => {
    // Trial users get AI access
    if (isTrialActive) return true;
    
    // Check if user has any AI Therappi subscription or add-on
    return isSubscribed && (
      productId === PRODUCTS.AI_THERAPPI_STANDALONE ||
      productId === PRODUCTS.AI_THERAPPI_FOUNDATIONS_ADDON ||
      productId === PRODUCTS.AI_THERAPPI_MASTERY_ADDON ||
      purchasedProducts.includes(PRODUCTS.AI_THERAPPI_STANDALONE)
    );
  };

  const checkCredits = async () => {
    try {
      if (!user) return;
      
      // Get current session to ensure we have a valid token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // No active session - silently return
        return;
      }
      
      const { data, error } = await supabase.functions.invoke('manage-credits', {
        body: { action: 'check_and_reset' },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      if (error) {
        // Error checking credits - handled silently
      } else {
        setMonthlyCredits(data?.monthly_credits || 0);
        setPurchasedCredits(data?.purchased_credits || 0);
        setTotalCredits(data?.total_credits || 0);
        setCreditsResetDate(data?.reset_date || null);
        setIsTrialActive(data?.is_trial_active || false);
        setTrialEndDate(data?.trial_end_date || null);
      }
    } catch {
      // Error in checkCredits - handled silently
    }
  };

  const deductCredits = async (amount: number = 1): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('manage-credits', {
        body: { action: 'deduct', amount }
      });
      
      if (error) {
        // Error deducting credits
        return false;
      }
      
      if (!data?.success) {
        // Credit deduction failed
        return false;
      }
      
      setMonthlyCredits(data.monthly_credits);
      setPurchasedCredits(data.purchased_credits);
      setTotalCredits(data.total_credits);
      return true;
    } catch {
      // Error deducting credits
      return false;
    }
  };

  const purchaseCredits = async (priceId: string): Promise<string> => {
    const { data, error } = await supabase.functions.invoke('purchase-credits', {
      body: { price_id: priceId }
    });

    if (error) throw error;
    return data.url;
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        setIsLoading(false);
        
        // Check subscription when user logs in
        if (session?.user && event === 'SIGNED_IN') {
          setTimeout(checkSubscription, 1000);
        }
        
        // Clear subscription and purchase data when user logs out
        if (event === 'SIGNED_OUT') {
          setIsSubscribed(false);
          setProductId(null);
          setSubscriptionEnd(null);
          setPurchasedProducts([]);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Check subscription and credits periodically for logged-in users
  useEffect(() => {
    if (!user) return;

    // Initial check
    checkSubscription();
    checkCredits();

    // Grant monthly credits when user has AI Therappi access
    const grantMonthlyCreditsIfNeeded = async () => {
      if (hasAITherappiAccess() && totalCredits === 0) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) return;
          
          await supabase.functions.invoke('manage-credits', {
            body: { action: 'grant_monthly' },
            headers: {
              Authorization: `Bearer ${session.access_token}`
            }
          });
          checkCredits();
        } catch {
          // Error granting monthly credits - handled silently
        }
      }
    };

    grantMonthlyCreditsIfNeeded();

    // Set up periodic checks every 60 seconds
    const interval = setInterval(() => {
      checkSubscription();
      checkCredits();
    }, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const value = {
    user,
    isSubscribed,
    productId,
    subscriptionEnd,
    purchasedProducts,
    isLoading,
    monthlyCredits,
    purchasedCredits,
    totalCredits,
    creditsResetDate,
    isTrialActive,
    trialEndDate,
    checkSubscription,
    checkCredits,
    deductCredits,
    createCheckout,
    createPayment,
    purchaseCredits,
    openCustomerPortal,
    hasPremiumAccess,
    hasFeatureAccess,
    hasAITherappiAccess,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export { PRODUCTS, PRICES };